import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMediaQuery } from 'react-responsive';


ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const languageColors = {
  TypeScript: '#4a9eda',
  JavaScript: '#f65403',
  Python: '#ff8901',
  CSS: '#ffbd28',
  HTML: '#2d2d2d',
};

const formatValue = (value) => {
  if (value >= 1000) {
    return Math.floor(value / 1000) + 'k'; 
  }
  return value.toString(); 
};

const GithubBarChart = ({ repoLanguages }) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const reposWithTotalBytes = repoLanguages.map(repo => ({
    name: repo.name,
    totalBytes: Object.values(repo.languages).reduce((sum, bytes) => sum + bytes, 0),
    languages: repo.languages,
  }));

  const sortedRepos = reposWithTotalBytes.sort((a, b) => b.totalBytes - a.totalBytes);

  const chartData = {
    labels: sortedRepos.map(repo => repo.name),
    datasets: Object.keys(languageColors).map(lang => ({
      label: lang,
      data: sortedRepos.map(repo => {
        const pct = repo.totalBytes > 0 ? (repo.languages[lang] || 0) / repo.totalBytes * 100 : 0;
        return Math.round(pct * 10) / 10;
      }),
      rawData: sortedRepos.map(repo => repo.languages[lang] || 0),
      backgroundColor: languageColors[lang],
      stack: 'Stack 0',
    })),
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const raw = context.dataset.rawData?.[context.dataIndex] ?? 0;
            const pct = context.parsed.x;
            if (raw === 0) return null;
            return `${context.dataset.label}: ${pct}% (${formatValue(raw)} bytes)`;
          },
        },
      },
      datalabels: {
        display: !isMobile,
        anchor: 'center',
        align: 'center',
        formatter: (value) => {
          if (value === 0) return '';
          if (value < 5) return '';
          return `${value}%`;
        },
        color: 'white',
        font: {
          weight: 'bold',
          size: 10,
        },
        clip: true,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Language Breakdown (%)',
        },
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
        },
        grid: {
          display: false,
        },
      },
      y: {
        title: {
          display: false,
        },
        stacked: true,
        grid: {
          display: false,
        },
        position: 'right',
      },
    },
  };

  return (
    <div className="my-5">
      <div className="flex justify-center mb-2 flex-wrap">
        {Object.entries(languageColors).map(([lang, color]) => (
          <div key={lang} className="flex items-center mx-2">
            <div className="w-4 h-4" style={{ backgroundColor: color }}></div>
            <span className="ml-2 text-sm">{lang}</span>
          </div>
        ))}
      </div>
      <div className="w-full h-72 md:h-96">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default GithubBarChart;
